/**
 * Send Forecast — operational visibility into the workflow drip dispatcher.
 *
 * Five tabbed views over one /api/marketing/forecast payload:
 *   A. Sent in last 24h — who got what, grouped by step
 *   B. Due in next 24h — what's about to go out, grouped by step
 *   C. Stalled — never moved past step 0 in >7d, or explicitly errored
 *   D. By contact — per-contact funnel position
 *   E. By step — funnel view: sent so far + still queued ahead
 *
 * Shipped 11-May-2026 in response to the user's question "have any
 * step 2 emails gone out yet at all?" — they correctly diagnosed
 * that step 1 was monopolising the daily cap. The dispatcher fix
 * (fair-share allocation) is in routes/marketing/extras.py; this
 * page is the operator's eyes for monitoring its effect.
 */
import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, Envelope, Clock,
         User, Stack } from "@phosphor-icons/react";

const TABS = [
  { id: "sent_24h",    label: "Sent today",         icon: Envelope },
  { id: "due_24h",     label: "Scheduled — next 24h", icon: Clock },
  { id: "step_funnel", label: "By step (funnel)",   icon: Stack },
  { id: "per_contact", label: "By contact",         icon: User },
];

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function StatusPill({ status }) {
  const colour = {
    sent:      "#16A34A",
    delivered: "#16A34A",
    queued:    "#525252",
    bounced:   "#DC2626",
    dropped:   "#DC2626",
    blocked:   "#DC2626",
    spam:      "#DC2626",
    failed:    "#DC2626",
    errored:   "#DC2626",
    active:    "#0055FF",
    paused:    "#D97706",
  }[(status || "").toLowerCase()] || "#525252";
  return (
    <span
      className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded"
      style={{ background: `${colour}15`, color: colour }}
      data-testid={`status-pill-${status}`}
    >
      {status || "?"}
    </span>
  );
}

function groupBy(rows, key) {
  const out = new Map();
  for (const r of rows) {
    const k = r[key];
    if (!out.has(k)) out.set(k, []);
    out.get(k).push(r);
  }
  return out;
}

export default function SendForecastPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("sent_24h");
  const [workflowFilter, setWorkflowFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/marketing/forecast");
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load forecast: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Workflow filter options (built from any tab that has rows).
  const workflowOptions = useMemo(() => {
    if (!data) return [];
    const seen = new Map();
    ["sent_24h", "due_24h", "step_funnel", "per_contact", "stalled"].forEach(t => {
      (data.tabs[t] || []).forEach(r => {
        if (r.workflow_id && !seen.has(r.workflow_id)) {
          seen.set(r.workflow_id, r.workflow_name);
        }
      });
    });
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [data]);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    const rows = data.tabs[activeTab] || [];
    if (!workflowFilter) return rows;
    return rows.filter(r => r.workflow_id === workflowFilter);
  }, [data, activeTab, workflowFilter]);

  if (loading) {
    return (
      <div className="p-8 text-sm text-[#525252]" data-testid="forecast-loading">
        Loading forecast…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="p-8 text-sm text-[#DC2626]" data-testid="forecast-error">
        Could not load forecast.
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="send-forecast-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[2px] text-[#A3A3A3] mb-1">
            Marketing · Workflow Dispatcher
          </div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Send Forecast</h1>
          <div className="text-sm text-[#525252] mt-1">
            Live view of what the workflow dispatcher has sent, what's due, and where each contact is.
            Generated {fmtDateTime(data.generated_at)}.
          </div>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#0055FF] text-[#0055FF] text-xs font-bold uppercase tracking-wider hover:bg-[#F0F7FF]"
          data-testid="forecast-refresh-btn"
        >
          <ArrowsClockwise size={14} weight="bold" /> Refresh
        </button>
      </div>

      {/* Cap-aware headline — the truth about what's actually firing.
          Same pattern as the morning digest (v1.30): big number is the
          cap-respecting count; raw queue depth gets demoted to context. */}
      {data.cap && <CapBanner cap={data.cap} />}


      {/* Tab strip */}
      <div className="flex flex-wrap gap-1 border-b border-[#E5E5E5] mb-4">
        {TABS.map(t => {
          const Icon = t.icon;
          const count = data.counts[t.id] || 0;
          const active = t.id === activeTab;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 flex items-center gap-2 transition ${
                active
                  ? "border-[#0055FF] text-[#0055FF] bg-[#F0F7FF]"
                  : "border-transparent text-[#525252] hover:text-[#0A0A0A]"
              }`}
              data-testid={`forecast-tab-${t.id}`}
            >
              <Icon size={14} weight={active ? "fill" : "regular"} />
              {t.label}
              <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${
                active ? "bg-[#0055FF] text-white" : "bg-[#E5E5E5] text-[#525252]"
              }`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Workflow filter */}
      {workflowOptions.length > 1 && (
        <div className="flex items-center gap-2 mb-4">
          <label className="text-[10px] uppercase tracking-[2px] text-[#A3A3A3]">Workflow filter</label>
          <select
            value={workflowFilter}
            onChange={e => setWorkflowFilter(e.target.value)}
            className="border border-[#E5E5E5] px-3 py-1.5 text-sm bg-white"
            data-testid="forecast-workflow-filter"
          >
            <option value="">All workflows</option>
            {workflowOptions.map(w => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Tab content */}
      {activeTab === "step_funnel" && (
        <StepFunnelTable rows={filteredRows} />
      )}
      {(activeTab === "sent_24h" || activeTab === "due_24h") && (
        <EmailListTable
          rows={filteredRows}
          timeField={activeTab === "sent_24h" ? "queued_at" : "next_action_at"}
          timeLabel={activeTab === "sent_24h" ? "Sent at" : "Due at"}
        />
      )}
      {activeTab === "per_contact" && (
        <PerContactTable rows={filteredRows} />
      )}
      {activeTab === "stalled" && (
        <StalledTable rows={filteredRows} />
      )}
    </div>
  );
}

// ── Sub-tables ─────────────────────────────────────────────────────────────

function StepFunnelTable({ rows }) {
  if (!rows.length) return <Empty msg="No active workflows with email steps." />;
  const grouped = groupBy(rows, "workflow_name");
  return (
    <div className="space-y-6" data-testid="step-funnel">
      {[...grouped.entries()].map(([workflow, items]) => (
        <div key={workflow} className="border border-[#E5E5E5]">
          <div className="bg-[#FAFAFA] px-4 py-2 border-b border-[#E5E5E5]">
            <div className="font-bold text-sm text-[#0A0A0A]">{workflow}</div>
            <div className="text-[10px] uppercase tracking-[2px] text-[#A3A3A3] mt-0.5">
              {items.length} email step{items.length !== 1 ? "s" : ""}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-[2px] text-[#525252]">
              <tr>
                <th className="text-left px-4 py-2 border-b border-[#E5E5E5] w-20">Step</th>
                <th className="text-left px-4 py-2 border-b border-[#E5E5E5]">Subject</th>
                <th className="text-right px-4 py-2 border-b border-[#E5E5E5] w-32">Sent so far</th>
                <th className="text-right px-4 py-2 border-b border-[#E5E5E5] w-40">Queued at this step</th>
                <th className="text-right px-4 py-2 border-b border-[#E5E5E5] w-40">Ahead of this step</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={`${it.workflow_id}-${it.step_index}`} className="hover:bg-[#FAFAFA]">
                  <td className="px-4 py-2 border-b border-[#F5F5F5] font-mono text-xs">{it.step_index}</td>
                  <td className="px-4 py-2 border-b border-[#F5F5F5]">{it.subject || "(untitled)"}</td>
                  <td className="px-4 py-2 border-b border-[#F5F5F5] text-right font-mono">{it.sent_so_far.toLocaleString()}</td>
                  <td className="px-4 py-2 border-b border-[#F5F5F5] text-right font-mono">{it.queued_at_step.toLocaleString()}</td>
                  <td className="px-4 py-2 border-b border-[#F5F5F5] text-right font-mono text-[#A3A3A3]">{it.ahead_of_step.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function EmailListTable({ rows, timeField, timeLabel }) {
  if (!rows.length) return <Empty msg="No rows in this window." />;
  // Group by step_label so the operator sees what's happening per step
  const grouped = groupBy(rows, "step_label");
  return (
    <div className="space-y-6" data-testid="email-list-table">
      {[...grouped.entries()].map(([stepLabel, items]) => (
        <div key={stepLabel} className="border border-[#E5E5E5]">
          <div className="bg-[#F0F7FF] px-4 py-2 border-b border-[#E5E5E5] flex items-center justify-between">
            <div className="font-bold text-sm text-[#0A0A0A]">{stepLabel || "(no step label)"}</div>
            <div className="text-[10px] uppercase tracking-[2px] text-[#525252]">{items.length} rows</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-[2px] text-[#525252]">
              <tr>
                <th className="text-left px-4 py-2 border-b border-[#E5E5E5] w-44">{timeLabel}</th>
                <th className="text-left px-4 py-2 border-b border-[#E5E5E5]">Contact</th>
                <th className="text-left px-4 py-2 border-b border-[#E5E5E5]">Workflow</th>
                <th className="text-left px-4 py-2 border-b border-[#E5E5E5] w-24">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 100).map(it => (
                <tr key={it.id || it.enrollment_id} className="hover:bg-[#FAFAFA]">
                  <td className="px-4 py-2 border-b border-[#F5F5F5] font-mono text-xs">{fmtDateTime(it[timeField])}</td>
                  <td className="px-4 py-2 border-b border-[#F5F5F5]">
                    <div className="text-sm">{it.contact_name || <span className="text-[#A3A3A3]">(no name)</span>}</div>
                    <div className="text-xs text-[#A3A3A3]">{it.contact_email || "—"}</div>
                  </td>
                  <td className="px-4 py-2 border-b border-[#F5F5F5] text-xs">{it.workflow_name || "—"}</td>
                  <td className="px-4 py-2 border-b border-[#F5F5F5]"><StatusPill status={it.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length > 100 && (
            <div className="text-[10px] text-[#A3A3A3] px-4 py-2 italic">
              Showing 100 of {items.length} rows in this step bucket.
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function PerContactTable({ rows }) {
  if (!rows.length) return <Empty msg="No contacts enrolled in any workflow." />;
  return (
    <div className="border border-[#E5E5E5]" data-testid="per-contact-table">
      <table className="w-full text-sm">
        <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-[2px] text-[#525252]">
          <tr>
            <th className="text-left px-4 py-2 border-b border-[#E5E5E5]">Contact</th>
            <th className="text-left px-4 py-2 border-b border-[#E5E5E5]">Workflow</th>
            <th className="text-left px-4 py-2 border-b border-[#E5E5E5]">Current step</th>
            <th className="text-left px-4 py-2 border-b border-[#E5E5E5] w-40">Next action due</th>
            <th className="text-left px-4 py-2 border-b border-[#E5E5E5] w-24">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 300).map(it => (
            <tr key={it.enrollment_id} className="hover:bg-[#FAFAFA]">
              <td className="px-4 py-2 border-b border-[#F5F5F5]">
                <div className="text-sm">{it.contact_name || <span className="text-[#A3A3A3]">(no name)</span>}</div>
                <div className="text-xs text-[#A3A3A3]">{it.contact_email || "—"}</div>
              </td>
              <td className="px-4 py-2 border-b border-[#F5F5F5] text-xs">{it.workflow_name || "—"}</td>
              <td className="px-4 py-2 border-b border-[#F5F5F5] text-xs">{it.step_label || `Step ${it.step_index}`}</td>
              <td className="px-4 py-2 border-b border-[#F5F5F5] font-mono text-xs">{fmtDateTime(it.next_action_at)}</td>
              <td className="px-4 py-2 border-b border-[#F5F5F5]"><StatusPill status={it.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 300 && (
        <div className="text-[10px] text-[#A3A3A3] px-4 py-2 italic">
          Showing 300 of {rows.length} active enrolments. Use the workflow filter to narrow down.
        </div>
      )}
    </div>
  );
}

function StalledTable({ rows }) {
  if (!rows.length) return <Empty msg="Nothing stalled. 🎉" />;
  return (
    <div className="space-y-4" data-testid="stalled-table">
      <div className="text-xs text-[#525252]">
        Contacts still at <strong>step 0</strong> after 7+ days OR enrolments
        with status <code className="bg-[#FFF7EE] px-1 py-0.5 text-[10px]">errored</code>.
      </div>
      <div className="border border-[#E5E5E5]">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-[2px] text-[#525252]">
            <tr>
              <th className="text-left px-4 py-2 border-b border-[#E5E5E5]">Contact</th>
              <th className="text-left px-4 py-2 border-b border-[#E5E5E5]">Workflow</th>
              <th className="text-left px-4 py-2 border-b border-[#E5E5E5]">Current step</th>
              <th className="text-left px-4 py-2 border-b border-[#E5E5E5] w-40">Enrolled</th>
              <th className="text-left px-4 py-2 border-b border-[#E5E5E5] w-24">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 300).map(it => (
              <tr key={it.enrollment_id} className="hover:bg-[#FFF7EE]">
                <td className="px-4 py-2 border-b border-[#F5F5F5]">
                  <div className="text-sm">{it.contact_name || <span className="text-[#A3A3A3]">(no name)</span>}</div>
                  <div className="text-xs text-[#A3A3A3]">{it.contact_email || "—"}</div>
                </td>
                <td className="px-4 py-2 border-b border-[#F5F5F5] text-xs">{it.workflow_name || "—"}</td>
                <td className="px-4 py-2 border-b border-[#F5F5F5] text-xs">{it.step_label || `Step ${it.step_index}`}</td>
                <td className="px-4 py-2 border-b border-[#F5F5F5] font-mono text-xs">{fmtDateTime(it.enrolled_at)}</td>
                <td className="px-4 py-2 border-b border-[#F5F5F5]"><StatusPill status={it.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div className="p-8 text-center text-sm text-[#A3A3A3] border border-[#E5E5E5] bg-[#FAFAFA]">
      {msg}
    </div>
  );
}

function CapBanner({ cap }) {
  const bandColor = {
    green:    "#16A34A",
    amber:    "#D97706",
    red:      "#DC2626",
    starter:  "#0055FF",
    override: "#7C3AED",
  }[cap.band] || "#525252";
  const bandLabel = {
    green:    "Green — healthy",
    amber:    "Amber — caution",
    red:      "Red — frozen",
    starter:  "Starter — building signal",
    override: "Operator override",
  }[cap.band] || (cap.band || "?");

  const willSendCopy = cap.effective_cap === 0
    ? <>Outbound is currently <strong>paused</strong>.</>
    : (
      <>
        Up to <strong>{cap.will_send_today}</strong> email
        {cap.will_send_today === 1 ? "" : "s"} will send in the next 24 hours,
        capped by the daily brake.
      </>
    );

  return (
    <div className="mb-4" data-testid="forecast-cap-banner">
      <div className="border" style={{ borderColor: bandColor }}>
        <div className="px-4 py-3" style={{ background: bandColor, color: "white" }}>
          <div className="text-[10px] uppercase tracking-[2px] opacity-90">
            Today&apos;s plan — capped by the daily brake
          </div>
          <div className="text-[32px] font-bold leading-tight mt-0.5">
            {cap.effective_cap === 0 ? "0" : cap.will_send_today}
          </div>
          <div className="text-xs opacity-90 mt-1">{willSendCopy}</div>
        </div>
        <div className="bg-[#FAFAFA] px-4 py-2 text-xs text-[#525252] border-t border-[#E5E5E5]">
          Cap: <strong>{cap.effective_cap}/day</strong>
          &nbsp;·&nbsp; Already sent today: <strong>{cap.sent_today}</strong>
          &nbsp;·&nbsp; Remaining slots: <strong>{cap.cap_remaining}</strong>
          &nbsp;·&nbsp; Band: <strong style={{ color: bandColor }}>{bandLabel}</strong>
        </div>
      </div>
      {cap.queue_total_24h > cap.will_send_today && (
        <p className="text-xs text-[#525252] mt-2 leading-relaxed">
          <strong>Queue depth:</strong> {cap.queue_total_24h.toLocaleString()} email
          {cap.queue_total_24h === 1 ? "" : "s"} are technically due within the next 24 hours,
          but the dispatcher will only release {cap.will_send_today.toLocaleString()} today.
          The remaining {(cap.queue_total_24h - cap.will_send_today).toLocaleString()} carry
          forward to the following days at the same pace.
        </p>
      )}
      <p className="text-[11px] text-[#A3A3A3] mt-1 italic leading-relaxed">
        Why this number? {cap.band_reason}
      </p>
    </div>
  );
}
